import type { Editor } from '@tiptap/react'

export type SavedRange = { from: number; to: number }
export type SavedRangeRef = { current: SavedRange | null }

const getTargetRange = (editor: Editor, savedRef: SavedRangeRef): SavedRange | null => {
  const saved = savedRef.current
  if (saved && saved.from !== saved.to) return saved
  const { from, to } = editor.state.selection
  if (from !== to) return { from, to }
  return null
}

export const saveSelection = (editor: Editor, savedRef: SavedRangeRef) => {
  const { from, to } = editor.state.selection
  savedRef.current = { from, to }
  if (from !== to) editor.commands.setPersistentSelection({ from, to })
}

export const clearPersistentSelection = (editor: Editor) => {
  editor.commands.clearPersistentSelection()
}

export const applyTextStyleColor = (editor: Editor, savedRef: SavedRangeRef, color: string | null) => {
  const range = getTargetRange(editor, savedRef)
  if (!range) {
    if (color === null) editor.chain().unsetColor().run()
    else editor.chain().setColor(color).run()
    return
  }
  const { state, view } = editor
  const markType = state.schema.marks.textStyle
  if (!markType) return
  const tr = state.tr
  state.doc.nodesBetween(range.from, range.to, (node, pos) => {
    if (!node.isInline) return
    const from = Math.max(pos, range.from)
    const to = Math.min(pos + node.nodeSize, range.to)
    if (from >= to) return
    const existing = node.marks.find((m) => m.type === markType)
    tr.removeMark(from, to, markType)
    const nextAttrs: Record<string, unknown> = { ...(existing?.attrs ?? {}), color }
    if (Object.values(nextAttrs).some((v) => v != null)) {
      tr.addMark(from, to, markType.create(nextAttrs))
    }
  })
  if (tr.steps.length) view.dispatch(tr)
}

export const applyHighlightColor = (editor: Editor, savedRef: SavedRangeRef, color: string | null) => {
  const range = getTargetRange(editor, savedRef)
  if (!range) {
    if (color === null) editor.chain().unsetHighlight().run()
    else editor.chain().setHighlight({ color }).run()
    return
  }
  const { state, view } = editor
  const markType = state.schema.marks.highlight
  if (!markType) return
  const tr = state.tr
  state.doc.nodesBetween(range.from, range.to, (node, pos) => {
    if (!node.isInline) return
    const from = Math.max(pos, range.from)
    const to = Math.min(pos + node.nodeSize, range.to)
    if (from >= to) return
    tr.removeMark(from, to, markType)
    if (color !== null) tr.addMark(from, to, markType.create({ color }))
  })
  if (tr.steps.length) view.dispatch(tr)
}
